/**
 * ServiceProductDrawer — create/edit a catalog ServiceProduct in the shared
 * DrawerShell chrome (same drawer-first pattern as the Activities template
 * drawer). Replaces the 7-step wizard with one scroll: essentials + booking
 * mode, pricing, staffing & provider (no staff / internal / external),
 * conditional capacity + transfer sections, and an advanced fold. Every
 * services-only capability stays — booking modes, pricing bases, capacity
 * types, transfer logistics, external suppliers — the steps and the live
 * preview are what went away.
 */

import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from '@/core';
import { cn } from '@/lib/utils';
import { useInfiniteSuppliers } from '@/modules/suppliers/hooks/useSuppliers';
import { DrawerShell } from '@/shared/components';
import { Input } from '@/theme/components/ui/input';
import { Label } from '@/theme/components/ui/label';
import { Button } from '@/theme/components/ui/button';
import { Textarea } from '@/theme/components/ui/textarea';
import { Switch } from '@/theme/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/theme/components/ui/select';
import type {
  ServiceProduct, ServiceProductFormData, ServiceType, BookingMode,
  PricingBasis, CapacityType, ResourceKind, TransferDirection,
} from '../types';

const schema = z.object({
  name: z.string().min(1, 'services.validation.nameRequired'),
  description: z.string().optional(),
  serviceType: z.enum(['transfer', 'beach_transfer', 'fnb', 'rental', 'wellness', 'extra', 'other']),
  bookingMode: z.enum(['one_off', 'on_request', 'scheduled', 'recurring', 'per_night', 'rental']),
  pricingBasis: z.enum(['per_person', 'per_seat', 'flat', 'per_leg', 'per_night', 'per_day', 'per_hour', 'percent']),
  basePrice: z.number().min(0),
  currency: z.string().min(1),
  taxable: z.boolean(),
  staffPurchasable: z.boolean(),
  staffBasePrice: z.number().min(0).optional().nullable(),
  capacityType: z.enum(['free_sale', 'limited', 'on_request', 'unit_stock']),
  maxCapacity: z.number().min(0).optional().nullable(),
  unitStock: z.number().min(0).optional().nullable(),
  resourceKind: z.enum(['none', 'vehicle', 'room', 'therapist']),
  staffing: z.enum(['none', 'internal', 'external']),
  supplier: z.string().optional().nullable(),
  isTransfer: z.boolean(),
  defaultDirection: z.enum(['arrival', 'departure', 'point_to_point', 'round_trip', '']).optional(),
  originLabel: z.string().optional(),
  destLabel: z.string().optional(),
  requiresFlight: z.boolean(),
  isActive: z.boolean(),
});

type FormSchema = z.infer<typeof schema>;
type Staffing = FormSchema['staffing'];

const SERVICE_TYPES: ServiceType[] = ['transfer', 'beach_transfer', 'fnb', 'rental', 'wellness', 'extra', 'other'];
const BOOKING_MODES: BookingMode[] = ['one_off', 'on_request', 'scheduled', 'recurring', 'per_night', 'rental'];
const PRICING_BASES: PricingBasis[] = ['per_person', 'per_seat', 'flat', 'per_leg', 'per_night', 'per_day', 'per_hour', 'percent'];
const CAPACITY_TYPES: CapacityType[] = ['free_sale', 'limited', 'on_request', 'unit_stock'];
const RESOURCE_KINDS: ResourceKind[] = ['none', 'vehicle', 'room', 'therapist'];
const DIRECTIONS: TransferDirection[] = ['arrival', 'departure', 'point_to_point', 'round_trip'];
const STAFFING_OPTIONS: Staffing[] = ['none', 'internal', 'external'];

/** providerType + requiresStaff → the drawer's single staffing choice. */
function toStaffing(product: ServiceProduct): Staffing {
  if (product.providerType === 'external') return 'external';
  return product.requiresStaff ? 'internal' : 'none';
}

/** Sensible per-type presets applied while creating (user can override). */
const TYPE_DEFAULTS: Partial<Record<ServiceType, Partial<FormSchema>>> = {
  transfer: { isTransfer: true, pricingBasis: 'per_person' },
  beach_transfer: { isTransfer: true },
  fnb: { pricingBasis: 'per_person' },
  rental: { bookingMode: 'rental', capacityType: 'unit_stock', pricingBasis: 'per_day', staffing: 'none' },
  wellness: { resourceKind: 'therapist' },
};

const EMPTY: FormSchema = {
  name: '', description: '', serviceType: 'transfer', bookingMode: 'on_request',
  pricingBasis: 'flat', basePrice: 0, currency: 'EUR', taxable: true,
  staffPurchasable: false, staffBasePrice: null,
  capacityType: 'free_sale', maxCapacity: null, unitStock: null, resourceKind: 'none',
  staffing: 'internal', supplier: null, isTransfer: true, defaultDirection: '',
  originLabel: '', destLabel: '', requiresFlight: false, isActive: true,
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When set, edit this product; otherwise create. */
  product?: ServiceProduct;
  onSubmit: (data: ServiceProductFormData) => void;
  isLoading?: boolean;
}

export function ServiceProductDrawer({ open, onOpenChange, product, onSubmit, isLoading }: Props) {
  const { t } = useTranslation();
  const { suppliers } = useInfiniteSuppliers({ type: 'services' });
  const [advanced, setAdvanced] = useState(false);

  const { register, handleSubmit, watch, setValue, reset, formState: { errors } } = useForm<FormSchema>({
    resolver: zodResolver(schema),
    defaultValues: EMPTY,
  });

  // Reset / seed on open.
  useEffect(() => {
    if (!open) return;
    setAdvanced(false);
    reset(product
      ? {
          name: product.name,
          description: product.description ?? '',
          serviceType: product.serviceType,
          bookingMode: product.bookingMode,
          pricingBasis: product.pricingBasis,
          basePrice: product.basePrice,
          currency: product.currency,
          taxable: product.taxable,
          staffPurchasable: product.staffPurchasable ?? false,
          staffBasePrice: product.staffBasePrice ?? null,
          capacityType: product.capacityType,
          maxCapacity: product.maxCapacity ?? null,
          unitStock: product.unitStock ?? null,
          resourceKind: product.resourceKind,
          staffing: toStaffing(product),
          supplier: product.supplier ?? null,
          isTransfer: product.isTransfer,
          defaultDirection: (product.defaultDirection ?? '') as FormSchema['defaultDirection'],
          originLabel: product.originLabel ?? '',
          destLabel: product.destLabel ?? '',
          requiresFlight: product.requiresFlight,
          isActive: product.isActive,
        }
      : EMPTY);
  }, [open, product, reset]);

  const values = watch();
  const isEdit = Boolean(product);

  const changeType = (st: ServiceType) => {
    setValue('serviceType', st);
    if (isEdit) return;
    // Presets only while creating — never rewrite an existing product.
    const presets = TYPE_DEFAULTS[st] ?? {};
    setValue('isTransfer', st === 'transfer' || st === 'beach_transfer');
    for (const [k, v] of Object.entries(presets)) {
      if (k === 'isTransfer') continue;
      setValue(k as keyof FormSchema, v as never);
    }
  };

  const submit = (data: FormSchema) => {
    const { staffing, ...rest } = data;
    onSubmit({
      ...rest,
      providerType: staffing === 'external' ? 'external' : 'internal',
      requiresStaff: staffing !== 'none',
      supplier: staffing === 'external' ? data.supplier ?? null : null,
    } as ServiceProductFormData);
  };

  return (
    <DrawerShell
      open={open}
      onOpenChange={onOpenChange}
      wide
      title={isEdit
        ? t('services.form.editTitle', 'Edit service')
        : t('services.form.createTitle', 'New service')}
      subtitle={t('services.form.subtitle', 'Catalog definition — what is sold and how.')}
      footer={
        <>
          <Button variant="outline" className="h-9 flex-1" onClick={() => onOpenChange(false)} disabled={isLoading}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button className="h-9 flex-[2]" onClick={handleSubmit(submit)} disabled={isLoading}>
            {isLoading
              ? t('common.loading', 'Saving…')
              : isEdit ? t('common.save', 'Save') : t('services.catalog.add', 'New service')}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* ── Essentials ── */}
        <Section title={t('services.form.basics', 'Basics')}>
          <div>
            <Label>{t('services.form.name', 'Name')} *</Label>
            <Input {...register('name')} />
            {errors.name && <p className="mt-1 text-sm text-destructive">{t(errors.name.message!)}</p>}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('services.form.type', 'Type')}>
              <EnumSelect value={values.serviceType} onChange={(x) => changeType(x as ServiceType)} options={SERVICE_TYPES} label={(o) => t(`services.serviceTypes.${o}`, o)} />
            </Field>
            <div className="sm:col-span-2">
              <Label>{t('services.form.description', 'Description')}</Label>
              <Textarea rows={2} {...register('description')} />
            </div>
          </div>
          <div>
            <Label>{t('services.form.bookingMode', 'Booking mode')}</Label>
            <div className="mt-1.5 grid grid-cols-2 gap-2">
              {BOOKING_MODES.map((m) => (
                <RadioCard
                  key={m}
                  selected={values.bookingMode === m}
                  onClick={() => setValue('bookingMode', m)}
                  label={t(`services.bookingModes.${m}`, m)}
                  hint={t(`services.bookingModeHints.${m}`, '')}
                />
              ))}
            </div>
            <p className="mt-2 rounded-md border border-dashed border-sun-300 bg-sun-50/50 px-3 py-2 text-xs text-muted-foreground dark:bg-sun-500/10">
              {t('services.form.modeNote', 'Scheduled and recurring services get dated runs from "New schedule" in Runs & calendar. On-request and per-night skip fixed times.')}
            </p>
          </div>
        </Section>

        {/* ── Pricing ── */}
        <Section title={t('services.form.pricing', 'Pricing')}>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>{t('services.form.basePrice', 'Price')}</Label>
              <Input type="number" step="0.01" min="0" {...register('basePrice', { valueAsNumber: true })} />
            </div>
            <Field label={t('services.form.pricingBasis', 'Pricing basis')}>
              <EnumSelect value={values.pricingBasis} onChange={(x) => setValue('pricingBasis', x as PricingBasis)} options={PRICING_BASES} label={(o) => t(`services.pricingBases.${o}`, o)} />
            </Field>
          </div>
          <ToggleField label={t('services.form.taxable', 'Taxable')} checked={values.taxable} onChange={(x) => setValue('taxable', x)} />
          <ToggleField
            label={t('services.form.staffPurchasable', 'Staff can purchase')}
            checked={values.staffPurchasable}
            onChange={(x) => setValue('staffPurchasable', x)}
          />
          {values.staffPurchasable && (
            <div>
              <Label>{t('services.form.staffBasePrice', 'Staff price (optional)')}</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder={t('services.form.staffPriceInherit', 'Inherits the base price')}
                {...register('staffBasePrice', {
                  setValueAs: (v) => (v === '' || v == null ? null : Number(v)),
                })}
              />
            </div>
          )}
        </Section>

        {/* ── Capacity ── */}
        <Section title={t('services.form.capacity', 'Capacity & resource')}>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t('services.form.capacityType', 'Capacity')}>
              <EnumSelect value={values.capacityType} onChange={(x) => setValue('capacityType', x as CapacityType)} options={CAPACITY_TYPES} label={(o) => t(`services.capacityTypes.${o}`, o)} />
            </Field>
            {values.capacityType === 'limited' && (
              <div>
                <Label>{t('services.form.maxCapacity', 'Max seats')}</Label>
                <Input type="number" min="0" {...register('maxCapacity', { setValueAs: (x) => (x === '' || x == null ? null : Number(x)) })} />
              </div>
            )}
            {values.capacityType === 'unit_stock' && (
              <div>
                <Label>{t('services.form.unitStock', 'Units in stock')}</Label>
                <Input type="number" min="0" {...register('unitStock', { setValueAs: (x) => (x === '' || x == null ? null : Number(x)) })} />
              </div>
            )}
          </div>
        </Section>

        {/* ── Staffing & provider ── */}
        <Section title={t('services.form.staffing', 'Staffing & provider')}>
          <div className="grid gap-2">
            {STAFFING_OPTIONS.map((s) => (
              <RadioCard
                key={s}
                selected={values.staffing === s}
                onClick={() => setValue('staffing', s)}
                label={t(`services.form.staffing_${s}`, s === 'none' ? 'No staff needed' : s === 'internal' ? 'Internal staff' : 'External supplier')}
                hint={t(
                  `services.form.staffing_${s}Hint`,
                  s === 'none'
                    ? "Self-serve — runs don't ask for an assignee."
                    : s === 'internal'
                      ? 'Runs expect a staff member assigned from Runs & calendar.'
                      : 'Runs are fulfilled by an external provider.',
                )}
              />
            ))}
          </div>
          {values.staffing === 'external' && (
            <Field label={t('services.form.supplier', 'Supplier')}>
              <Select value={values.supplier ?? ''} onValueChange={(x) => setValue('supplier', x || null)}>
                <SelectTrigger><SelectValue placeholder={t('common.select', 'Select')} /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          )}
        </Section>

        {/* ── Transfer logistics (only when it is one) ── */}
        {values.isTransfer && (
          <Section title={t('services.form.transfer', 'Transfer logistics')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t('services.form.direction', 'Direction')}>
                <Select value={values.defaultDirection || ''} onValueChange={(x) => setValue('defaultDirection', x as TransferDirection)}>
                  <SelectTrigger><SelectValue placeholder={t('common.select', 'Select')} /></SelectTrigger>
                  <SelectContent>
                    {DIRECTIONS.map((d) => <SelectItem key={d} value={d}>{t(`services.directions.${d}`, d)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <ToggleField label={t('services.form.requiresFlight', 'Requires flight info')} checked={values.requiresFlight} onChange={(x) => setValue('requiresFlight', x)} />
              <div><Label>{t('services.form.origin', 'Origin')}</Label><Input {...register('originLabel')} /></div>
              <div><Label>{t('services.form.dest', 'Destination')}</Label><Input {...register('destLabel')} /></div>
            </div>
          </Section>
        )}

        {/* ── Advanced fold ── */}
        <div className="border-t border-sand-200 pt-3">
          <button
            type="button"
            onClick={() => setAdvanced((v) => !v)}
            className="flex w-full items-center justify-between text-[12.5px] font-semibold text-sand-700 hover:text-sand-900"
          >
            {t('services.form.advanced', 'Advanced options')}
            <ChevronDown className={cn('h-4 w-4 transition-transform', advanced && 'rotate-180')} />
          </button>
          {advanced && (
            <div className="mt-3 space-y-3">
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>{t('services.form.currency', 'Currency')}</Label>
                  <Input {...register('currency')} />
                </div>
                <Field label={t('services.form.resourceKind', 'Resource')}>
                  <EnumSelect value={values.resourceKind} onChange={(x) => setValue('resourceKind', x as ResourceKind)} options={RESOURCE_KINDS} label={(o) => t(`services.resourceKinds.${o}`, o)} />
                </Field>
              </div>
              <ToggleField label={t('services.form.isTransfer', 'This is a transfer')} checked={values.isTransfer} onChange={(x) => setValue('isTransfer', x)} />
              <ToggleField label={t('services.form.active', 'Active')} checked={values.isActive} onChange={(x) => setValue('isActive', x)} />
            </div>
          )}
        </div>

        {(values.bookingMode === 'scheduled' || values.bookingMode === 'recurring') && (
          <p className="rounded-md border border-dashed border-sun-300 bg-sun-50/50 px-3 py-2 text-xs text-muted-foreground dark:bg-sun-500/10">
            {t('services.form.reviewNote', 'Saved to catalog. No dates yet — schedule its runs from Runs & calendar.')}
          </p>
        )}
      </div>
    </DrawerShell>
  );
}

// ── small helpers ────────────────────────────────────────────────────────
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h4 className="text-[10.5px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{title}</h4>
      {children}
    </section>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <div><Label>{label}</Label>{children}</div>;
}

function ToggleField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-3 rounded-md border border-border px-3 py-2">
      <span className="text-sm font-medium">{label}</span>
      <Switch checked={checked} onCheckedChange={onChange} />
    </label>
  );
}

function RadioCard({ selected, onClick, label, hint }: {
  selected: boolean; onClick: () => void; label: string; hint?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cn(
        'rounded-lg border p-2.5 text-left',
        selected
          ? 'border-coral-500 bg-coral-50/60 ring-1 ring-coral-500 dark:bg-coral-500/10'
          : 'border-border hover:border-sand-300',
      )}
    >
      <p className="text-[13px] font-semibold">{label}</p>
      {hint ? <p className="mt-0.5 text-[11px] text-muted-foreground">{hint}</p> : null}
    </button>
  );
}

function EnumSelect({ value, onChange, options, label }: {
  value: string; onChange: (v: string) => void; options: string[]; label: (o: string) => string;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o} value={o}>{label(o)}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
